import { useState, useEffect } from 'react';
import { departmentsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Plus, Trash2, Building2, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

export default function DepartmentManagement() {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewDeptDialog, setShowNewDeptDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    try {
      setLoading(true);
      const res = await departmentsAPI.list();
      setDepartments(res.data || []);
    } catch (err) {
      console.error('Failed to load departments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDepartment = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Department name is required');
      return;
    }
    
    try {
      setSubmitting(true);
      await departmentsAPI.create(formData);
      
      // Reset form
      setFormData({ name: '', description: '' });
      setShowNewDeptDialog(false);
      
      // Reload departments
      await loadDepartments();
    } catch (err) {
      console.error('Failed to create department:', err);
      alert('Failed to create department. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteDepartment = async (deptId, deptName) => {
    if (!confirm(`Are you sure you want to delete the department "${deptName}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await departmentsAPI.delete(deptId);
      await loadDepartments();
    } catch (err) {
      console.error('Failed to delete department:', err);
      alert('Failed to delete department. It might be in use by existing tickets.');
    }
  };

  const filteredDepartments = departments.filter(dept =>
    searchQuery === '' ||
    dept.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    dept.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-auto">
      <div className="mb-4 md:mb-6">
        <h1 className="text-xl md:text-2xl font-bold mb-2 text-slate-800">Department Management</h1>
        <p className="text-sm md:text-base text-slate-500">Manage support departments for ticket routing</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mb-4 md:mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search departments..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-white border-slate-200 h-11 md:h-10"
          />
        </div>
        <Button
          onClick={() => setShowNewDeptDialog(true)}
          className="bg-cyan-500 hover:bg-cyan-600 rounded-full min-h-[44px] md:min-h-10"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Department
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {filteredDepartments.map((dept) => (
            <Card key={dept.id} className="bg-white border-slate-200 hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-cyan-600" />
                    </div>
                    <div>
                      <CardTitle className="text-base text-slate-800">{dept.name}</CardTitle>
                      <p className="text-xs text-slate-500 mt-1">
                        Created {new Date(dept.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-slate-400 hover:text-red-600 min-h-[44px] min-w-[44px] md:min-h-8 md:min-w-8"
                    onClick={() => handleDeleteDepartment(dept.id, dept.name)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600">
                  {dept.description || 'No description provided'}
                </p>
              </CardContent>
            </Card>
          ))}

          {filteredDepartments.length === 0 && (
            <div className="col-span-full text-center py-12 text-slate-400">
              <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No departments found</p>
            </div>
          )}
        </div>
      )}

      {/* New Department Dialog */}
      <Dialog open={showNewDeptDialog} onOpenChange={setShowNewDeptDialog}>
        <DialogContent className="bg-white border-slate-200 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-800">Add New Department</DialogTitle>
            <DialogDescription className="text-slate-600">
              Create a new department for ticket categorization
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateDepartment} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="dept-name" className="text-slate-700">
                Department Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="dept-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., IT Support, HR, Finance"
                className="bg-white border-slate-200"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dept-description" className="text-slate-700">Description</Label>
              <Textarea
                id="dept-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this department's responsibilities..."
                className="bg-white border-slate-200 min-h-[100px]"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setShowNewDeptDialog(false);
                  setFormData({ name: '', description: '' });
                }}
                className="text-slate-600 hover:text-slate-800"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-cyan-500 hover:bg-cyan-600 rounded-full"
                disabled={submitting || !formData.name.trim()}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Department
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
